// Team chat's channel dialogs (M10 round two, ported from the prototype's ChannelDialogs.tsx): make a channel, public or private. One
// management screen (the founder, 2026-10-12): a channel's own dialog behind the channels screen's Edit (name, topic, private or public with
// its people) and the delete's confirm (its messages and files go too; archive, in the row's menu, stays the mild option). #general is fixed.
// The server decides every write; the dialogs say what it will mean before. 2026-10-07 (DECISIONS "Private channels managed from outside:
// built"): a `team` holder manages a private channel he is not in from its dialog without reading it: its name, and people once nobody is in.
import { useState } from "react";
import { Button, Dialog, Input, Menu, Switch } from "@omnabase/ui";
import { channelSlug, type ChatOutsideRoom, type ChatRoom, type ChatRoomPatch } from "@omnabase/shared";
import { useSession } from "../../shell/store";
import { useWs } from "../../lib/nav";
import { addToChannel, createChannel, deleteChannel, patchChannel, removeFromChannel } from "../../lib/chat";
import { addable, channelNameProblem, privacyHelp } from "../../lib/chatRooms";

const KINDS = [
  { id: "public", title: "Public", sub: "Everyone in the workspace is in it and can read along." },
  { id: "private", title: "Private", sub: "Only the people you add. It does not show to anyone else." },
] as const;

/** the dialogs' immediate writes (a person added or removed): a failure is a toast, a success refetches the rooms */
export function useChannelWrites(reload: () => Promise<void>) {
  const { toast } = useSession();
  const { slug } = useWs();
  const run = (write: () => Promise<unknown>) => write().then(() => reload(), (e: Error) => toast(e.message, { kind: "error" }));
  return {
    patch: (roomId: string, patch: ChatRoomPatch) => run(() => patchChannel(slug, roomId, patch)),
    add: (roomId: string, userId: string) => run(() => addToChannel(slug, roomId, userId)),
    remove: (roomId: string, userId: string) => run(() => removeFromChannel(slug, roomId, userId)),
  };
}

export function NewChannelDialog({ rooms, onClose, onCreated }: { rooms: ChatRoom[]; onClose: () => void; onCreated: (room: ChatRoom) => void }) {
  const { toast } = useSession();
  const { slug } = useWs();
  const [name, setName] = useState("");
  const [topic, setTopic] = useState("");
  const [priv, setPriv] = useState(false);
  const [busy, setBusy] = useState(false);
  const problem = name.trim() ? channelNameProblem(name, rooms) : null;
  const create = async () => {
    if (problem || !name.trim() || busy) return;
    setBusy(true);
    try { onCreated(await createChannel(slug, { name, ...(topic.trim() ? { topic: topic.trim() } : {}), private: priv })); }
    catch (e) { toast((e as Error).message, { kind: "error" }); setBusy(false); }
  };
  return (
    <Dialog title="New channel" intro="A room for one topic or one group of people. The name is lower case with dashes; you can change it later." onClose={onClose} width={480}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!name.trim() || !!problem || busy} onClick={() => void create()}>Create channel</Button></>}>
      <div className="ob-dialog__field"><span className="ob-dialog__label">Name</span>
        <Input className="ob-input--text" prefix="#" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void create(); }} placeholder="design" aria-label="Name" autoFocus />
        <span className={problem ? "ob-dialog__hint ob-dialog__hint--error" : "ob-dialog__hint"}>{problem ?? (name.trim() ? `#${channelSlug(name)}` : "Short and specific: what is talked about here.")}</span>
      </div>
      <div className="ob-dialog__field"><span className="ob-dialog__label">Topic <span className="ob-dialog__optional">optional</span></span><input className="ob-dialog__input" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="What this channel is for" aria-label="Topic" /></div>
      <div role="radiogroup" aria-label="Who can see it" className="ob-dialog__choices">
        {KINDS.map((k) => { const on = (k.id === "private") === priv; return (
          <button key={k.id} type="button" role="radio" aria-checked={on} aria-label={k.title} className={`ob-choice-opt${on ? " ob-choice-opt--on" : ""}`} onClick={() => setPriv(k.id === "private")}>
            <span className="ob-choice-opt__radio" /><span className="ob-choice-opt__text"><span className="ob-choice-opt__title">{k.title}</span><span className="ob-choice-opt__sub">{k.sub}</span></span>
          </button>); })}
      </div>
    </Dialog>
  );
}

/** A channel's own dialog (Edit on the channels screen, `dialog=edit-<id>`): name, topic and private or public, kept with Save (Enter in the
 *  name saves too); a private channel's people are added and removed at once. */
export function EditChannelDialog({ channel: c, rooms, onClose, reload }: { channel: ChatRoom; rooms: ChatRoom[]; onClose: () => void; reload: () => Promise<void> }) {
  const { workspace, toast } = useSession();
  const { slug } = useWs();
  const writes = useChannelWrites(reload);
  const [name, setName] = useState(c.slug ?? "");
  const [topic, setTopic] = useState(c.topic ?? "");
  const [priv, setPriv] = useState(c.private);
  const [busy, setBusy] = useState(false);
  const problem = channelNameProblem(name, rooms, c.id);
  const patch: ChatRoomPatch = { ...(channelSlug(name) !== c.slug ? { name } : {}), ...(topic.trim() !== (c.topic ?? "") ? { topic: topic.trim() || null } : {}), ...(priv !== c.private ? { private: priv } : {}) };
  const save = async () => {
    if (problem || busy) return;
    if (!Object.keys(patch).length) { onClose(); return; }
    setBusy(true);
    try { await patchChannel(slug, c.id, patch); await reload(); onClose(); } catch (e) { toast((e as Error).message, { kind: "error" }); setBusy(false); }
  };
  const inside = (c.memberIds ?? []).map((id) => workspace.members.find((m) => m.userId === id)).filter((m) => !!m);
  const candidates = addable(c, workspace.members);
  return (
    <Dialog title={`Edit #${c.slug}`} intro="Everyone in it sees a new name or topic at once." onClose={onClose} width={520}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!!problem || busy} onClick={() => void save()}>Save</Button></>}>
      <form className="ob-dialog__form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <div className="ob-dialog__field"><span className="ob-dialog__label">Name</span>
          <Input className="ob-input--text" prefix="#" value={name} onChange={(e) => setName(e.target.value)} aria-label="Name" autoFocus />
          {problem && <span className="ob-dialog__hint ob-dialog__hint--error">{problem}</span>}
        </div>
        <div className="ob-dialog__field"><span className="ob-dialog__label">Topic <span className="ob-dialog__optional">optional</span></span><input className="ob-dialog__input" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="What this channel is for" aria-label="Topic" /></div>
        <div className="ob-form-row ob-form-row--inline"><div className="ob-form-row__text"><div className="ob-form-row__label">Private</div><div className="ob-form-row__help">{privacyHelp(priv, c.private)}</div></div><Switch checked={priv} label={`#${c.slug} is private`} onChange={setPriv} /></div>
        {c.private && priv && (
          <div className="ob-dialog__field"><span className="ob-dialog__label">People</span>
            <div className="ob-channel-row__people">
              <span className="ob-channel-row__names">{inside.map((m) => m.name).join(", ")}</span>
              <Menu trigger={<Button size="sm" icon="plus">Add people</Button>}
                sections={[{ label: `Add to #${c.slug}`, items: candidates.length ? candidates.map((m) => ({ id: m.userId, label: m.name, onSelect: () => void writes.add(c.id, m.userId) })) : [{ id: "none", label: "Everyone is already in this channel", disabled: true, onSelect: () => {} }] }]} />
              <Menu align="end" trigger={<Button size="sm">Remove</Button>}
                sections={[{ label: `Remove from #${c.slug}`, items: inside.map((m) => ({ id: m.userId, label: m.name, onSelect: () => void writes.remove(c.id, m.userId) })) }]} />
            </div>
          </div>
        )}
      </form>
    </Dialog>
  );
}

/** A private channel's dialog for a `team` holder outside it (2026-10-07): its name, and people while nobody is in; never a message or a
 *  name inside. */
export function EditOutsideDialog({ room: r, rooms, onClose, reload }: { room: ChatOutsideRoom; rooms: ChatRoom[]; onClose: () => void; reload: () => Promise<void> }) {
  const { workspace, me, toast } = useSession();
  const { slug } = useWs();
  const writes = useChannelWrites(reload);
  const [name, setName] = useState(r.name);
  const [busy, setBusy] = useState(false);
  const problem = channelNameProblem(name, rooms, r.id);
  const candidates = workspace.members.filter((m) => m.status === "active" && m.userId !== me.user.id);
  const save = async () => {
    if (problem || busy) return;
    if (channelSlug(name) === r.name) { onClose(); return; }
    setBusy(true);
    try { await patchChannel(slug, r.id, { name }); await reload(); onClose(); } catch (e) { toast((e as Error).message, { kind: "error" }); setBusy(false); }
  };
  return (
    <Dialog title={`Edit #${r.name}`} intro="You manage this channel without reading it: no messages, and nobody's name inside." onClose={onClose} width={520}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!!problem || busy} onClick={() => void save()}>Save</Button></>}>
      <form className="ob-dialog__form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <div className="ob-dialog__field"><span className="ob-dialog__label">Name</span>
          <Input className="ob-input--text" prefix="#" value={name} onChange={(e) => setName(e.target.value)} aria-label="Name" autoFocus />
          {problem && <span className="ob-dialog__hint ob-dialog__hint--error">{problem}</span>}
        </div>
        {r.memberCount === 0 && !r.archivedAt && (
          <div className="ob-dialog__field"><span className="ob-dialog__label">People</span>
            <div className="ob-channel-row__people">
              <span className="ob-channel-row__names">Nobody is in this channel. The people you add read its whole history; you stay outside.</span>
              <Menu trigger={<Button size="sm" icon="plus">Add people</Button>}
                sections={[{ label: `Add to #${r.name}`, items: candidates.map((m) => ({ id: m.userId, label: m.name, onSelect: () => void writes.add(r.id, m.userId) })) }]} />
            </div>
          </div>
        )}
      </form>
    </Dialog>
  );
}

/** The delete's confirm (`dialog=delete-<id>`): the channel goes with its messages and files for everyone; archive keeps them */
export function DeleteChannelDialog({ id, name, onClose, onDeleted }: { id: string; name: string; onClose: () => void; onDeleted: () => Promise<void> }) {
  const { toast } = useSession();
  const { slug } = useWs();
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try { await deleteChannel(slug, id); toast(`#${name} deleted`, { kind: "success" }); onClose(); await onDeleted(); } catch (e) { toast((e as Error).message, { kind: "error" }); setBusy(false); }
  };
  return (
    <Dialog title={`Delete #${name}?`} intro="Its messages and files are deleted for everyone, and this cannot be undone. Archive it instead to keep them." onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={busy} onClick={() => void confirm()}>Delete channel</Button></>} />
  );
}
